import { addSentryBreadcrumb } from '@frontend/web-utils';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isAnalyticsReleased, releaseDeferredMarketingScripts } from './analyticsWindow';

const ANALYTICS_RELEASE_MAX_WAIT_MS = 20_000;

export type AnalyticsReleaseReason = 'max_wait_timeout' | 'release_trigger';

export type AnalyticsState = { status: 'pending' } | { status: 'ready' };

const AnalyticsStateContext = createContext<MaybeUndefined<AnalyticsState>>(undefined);

export const useAnalyticsState = (): AnalyticsState => {
  const state = useContext(AnalyticsStateContext);
  if (!state) {
    throw new Error('useAnalyticsState must be used within an AnalyticsProvider');
  }
  return state;
};

type AnalyticsProviderProps = {
  children: ReactNode;
  maxWaitMs?: number;
  onRelease?: (reason: AnalyticsReleaseReason) => void;
  releaseTrigger: boolean;
};

const addAnalyticsReleaseBreadcrumb = ({ reason }: { reason: AnalyticsReleaseReason }) => {
  addSentryBreadcrumb({
    category: 'analytics.load',
    level: reason === 'max_wait_timeout' ? 'warning' : 'info',
    message:
      reason === 'max_wait_timeout'
        ? 'Deferred marketing scripts released by max wait timeout'
        : 'Deferred marketing scripts released by trigger',
  });
};

export const AnalyticsProvider = ({
  children,
  maxWaitMs = ANALYTICS_RELEASE_MAX_WAIT_MS,
  onRelease,
  releaseTrigger,
}: AnalyticsProviderProps) => {
  const [status, setStatus] = useState<AnalyticsState['status']>(isAnalyticsReleased() ? 'ready' : 'pending');
  const hasReleasedRef = useRef(false);

  const release = useCallback(
    (reason: AnalyticsReleaseReason) => {
      if (hasReleasedRef.current || isAnalyticsReleased()) {
        setStatus('ready');
        return;
      }

      // eslint-disable-next-line fp/no-mutation
      hasReleasedRef.current = true;
      addAnalyticsReleaseBreadcrumb({ reason });
      releaseDeferredMarketingScripts(reason);
      onRelease?.(reason);
      setStatus('ready');
    },
    [onRelease],
  );

  useEffect(() => {
    if (releaseTrigger) {
      release('release_trigger');
    }
  }, [release, releaseTrigger]);

  useEffect(() => {
    if (status === 'ready') {
      return;
    }

    const timeoutId = window.setTimeout(() => release('max_wait_timeout'), maxWaitMs);
    return () => window.clearTimeout(timeoutId);
  }, [maxWaitMs, release, status]);

  const value = useMemo(() => ({ status }), [status]);

  return <AnalyticsStateContext.Provider value={value}>{children}</AnalyticsStateContext.Provider>;
};
